import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Grid, FormControl, InputLabel, Select, MenuItem, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import AtelierForm from './AtelierForm';
import api from '../../api';
import { typesSeance, groupesMusculaires, exemplesAteliers, getSeanceTypeLabel, normalizeSeanceType } from './utils';

/**
 * SeanceForm
 * - charge settings (/muscu/settings) : ateliers, muscles, mappings, mode
 * - accepte prop seanceToken pour inclure le header x-seance-token lors du GET /muscu/settings
 * - normalise data (muscles/ateliers/mappings) pour garantir string-based comparisons
 *
 * Modifications demandées :
 * - afficher les types dans l'ordre SANTÉ, ESTHÉTIQUE, SPORT par défaut
 * - ajouter un bouton "EXEMPLES" à côté du select Type ouvrant une popup avec l'image correspondante
 *   (images placées dans public/assets : projet_sante.jpg, projet_esthe.jpg, projet_sport.jpg)
 */

export default function SeanceForm({ initial = {}, onSave, onOpenMonCarnet, seanceToken = null }) {
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [settings, setSettings] = useState({
    ateliers: [], muscles: [], mappings: [], mode: 'GUIDE'
  });

  // pastel palette (cycle)
  const pastelColors = [
    '#F3E8FF', // mauve clair
    '#E6F7ED', // vert pâle
    '#FFF4E6', // orange pâle
    '#E6F2FF', // bleu pâle
    '#FDEEF0', // rose pâle
    '#F0F7FF'  // cyan pâle
  ];

  const [date, setDate] = useState(initial.date ? initial.date.split('T')[0] : new Date().toISOString().slice(0, 10));

  // Ensure typesSeance order: SANTÉ, ESTHÉTIQUE, SPORT first (if present), then others
  const preferredOrder = useMemo(() => ['SANTÉ', 'ESTHETIQUE', 'SPORT'], []);
  const typesOrdered = useMemo(() => {
    const base = Array.isArray(typesSeance) ? typesSeance : [];
    const first = preferredOrder.filter(t => base.includes(t));
    const rest = base.filter(t => !preferredOrder.includes(t));
    return [...first, ...rest];
  }, [preferredOrder]);

  const [type, setType] = useState(normalizeSeanceType(initial.type || (typesOrdered.length ? typesOrdered[0] : 'SANTÉ')));

  // ateliers state : each item includes color property
  const initialAteliers = (initial.ateliers && initial.ateliers.length ? initial.ateliers : [{
    groupe: '',
    atelierNom: '',
    series: '',
    repetitions: '',
    poids: '',
    ressenti: 1
  }]).map((a, i) => ({ ...a, color: a.color || pastelColors[i % pastelColors.length] }));

  const [ateliers, setAteliers] = useState(initialAteliers);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  // examples dialog
  const [examplesOpen, setExamplesOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoadingSettings(true);

    // include seance token header if provided (allows public access via token)
    const config = seanceToken ? { headers: { 'x-seance-token': seanceToken } } : {};

    api.get('/muscu/settings', config).then(res => {
      if (!mounted) return;
      const data = res.data || {};

      // Normalisation : muscles and ateliers -> arrays of strings; mappings -> [{ muscle: string, ateliers: [string] }]
      const rawMuscles = Array.isArray(data.muscles) ? data.muscles : [];
      const rawAteliers = Array.isArray(data.ateliers) ? data.ateliers : [];
      const rawMappings = Array.isArray(data.mappings) ? data.mappings : [];

      const normMuscles = rawMuscles.map(m => (typeof m === 'string' ? m : (m?.name || m?.label || ''))).filter(Boolean);
      const normAteliers = rawAteliers.map(a => (typeof a === 'string' ? a : (a?.name || a?.label || ''))).filter(Boolean);
      const normMappings = rawMappings.map(m => {
        const mm = m?.muscle;
        const muscleName = typeof mm === 'string' ? mm : (mm?.name || mm?.label || '');
        const ateliersList = Array.isArray(m?.ateliers)
          ? m.ateliers.map(a => (typeof a === 'string' ? a : (a?.name || a?.label || ''))).filter(Boolean)
          : [];
        return { muscle: muscleName, ateliers: ateliersList };
      }).filter(m => m.muscle);

      setSettings({
        ateliers: normAteliers,
        muscles: normMuscles,
        mappings: normMappings,
        mode: data.mode || 'GUIDE'
      });
    }).catch(err => {
      console.error('Erreur chargement settings SeanceForm', err);
      // fallback to examples (kept as strings)
      setSettings({
        ateliers: exemplesAteliers.map(n => n),
        muscles: groupesMusculaires.map(n => n),
        mappings: [],
        mode: 'GUIDE'
      });
    }).finally(() => { if (mounted) setLoadingSettings(false); });
    return () => { mounted = false; };
    // include seanceToken in deps so we refetch when token changes
  }, [seanceToken]);

  // now muscleNames and atelierNames are strings (normalized)
  const atelierNames = useMemo(() => settings.ateliers || [], [settings.ateliers]);
  const muscleNames = useMemo(() => settings.muscles || [], [settings.muscles]);
  const mappings = settings.mappings || [];

  const handleAtelierChange = (index, partial) => {
    setAteliers(prev => {
      const next = prev.slice();
      next[index] = { ...next[index], ...partial };
      return next;
    });
  };

  const addAtelierRow = () => {
    setAteliers(prev => {
      const color = pastelColors[prev.length % pastelColors.length];
      return [...prev, { groupe: '', atelierNom: '', series: '', repetitions: '', poids: '', ressenti: 1, color }];
    });
  };

  const removeAtelierRow = (index) => {
    setAteliers(prev => prev.filter((_, i) => i !== index));
  };

  const validateBeforeSave = () => {
    if (!date) return 'Date requise';
    if (!Array.isArray(ateliers) || ateliers.length === 0) return 'Au moins un atelier requis';
    for (let i = 0; i < ateliers.length; i++) {
      const a = ateliers[i];
      if (!a.atelierNom && !a.groupe) return `Atelier #${i + 1} : préciser le groupe ou l'atelier`;
      if (settings.mode === 'GUIDE') {
        if (!a.groupe) return `Atelier #${i + 1} : choisissez d'abord un groupe (mode guidé)`;
        const mp = mappings.find(m => m.muscle === a.groupe);
        const allowed = Array.isArray(mp?.ateliers) ? mp.ateliers : [];
        if (a.atelierNom && !allowed.includes(a.atelierNom)) return `Atelier #${i + 1} : l'atelier choisi n'est pas autorisé pour le groupe "${a.groupe}"`;
      }
    }
    return null;
  };

  const handleSave = async () => {
    setMessage(null);
    const err = validateBeforeSave();
    if (err) {
      setMessage({ type: 'error', text: err });
      return;
    }

    const payload = {
      date: new Date(date).toISOString(),
      type: normalizeSeanceType(type),
      ateliers: ateliers.map(a => ({
        groupe: a.groupe || '',
        atelierNom: a.atelierNom || '',
        series: a.series === '' ? undefined : Number(a.series),
        repetitions: a.repetitions === '' ? undefined : Number(a.repetitions),
        poids: a.poids || '',
        ressenti: a.ressenti ?? 1
      }))
    };

    setSaving(true);
    try {
      const res = await onSave(payload);
      setMessage({ type: 'success', text: 'Séance enregistrée' });
      setAteliers([{ groupe: '', atelierNom: '', series: '', repetitions: '', poids: '', ressenti: 1, color: pastelColors[0] }]);
      if (onOpenMonCarnet) onOpenMonCarnet();
      return res;
    } catch (err) {
      console.error('Erreur save seance', err);
      setMessage({ type: 'error', text: err?.message || 'Erreur lors de l\'enregistrement' });
      throw err;
    } finally {
      setSaving(false);
    }
  };

  if (loadingSettings) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress /></Box>;

  const exampleImageForType = (t) => {
    if (!t) return '';
    const key = normalizeSeanceType(t);
    if (key === 'SANTÉ') return `${process.env.PUBLIC_URL}/assets/projet_sante.jpg`;
    if (key === 'ESTHETIQUE') return `${process.env.PUBLIC_URL}/assets/projet_esthe.jpg`;
    return `${process.env.PUBLIC_URL}/assets/projet_sport.jpg`;
  };

  return (
    <Paper sx={{ p: 2, mt: 2 }}>
      <Typography variant="h6" gutterBottom>Nouvelle séance</Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <TextField
            label="Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={date}
            onChange={e => setDate(e.target.value)}
            fullWidth
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <FormControl fullWidth>
              <InputLabel id="type-label">Type</InputLabel>
              <Select labelId="type-label" value={type} label="Type" onChange={e => setType(normalizeSeanceType(e.target.value))}>
                {typesOrdered.map(t => <MenuItem key={t} value={t}>{getSeanceTypeLabel(t)}</MenuItem>)}
              </Select>
            </FormControl>

            <Button
              type="button"
              variant="outlined"
              onClick={() => setExamplesOpen(true)}
              sx={{ whiteSpace: 'nowrap' }}
              startIcon={<MenuBookIcon />}
            >
              EXEMPLES
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12} sm={4}>
          <FormControl fullWidth>
            <InputLabel id="mode-label">Mode</InputLabel>
            <Select labelId="mode-label" value={settings.mode} label="Mode" disabled>
              <MenuItem value="GUIDE">GUIDÉ (réglages prof actifs)</MenuItem>
              <MenuItem value="EXPERT">EXPERT (libre)</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Box sx={{ mt: 2 }}>
        {ateliers.map((a, idx) => (
          <AtelierForm
            key={idx}
            index={idx}
            data={a}
            onChange={(partial) => handleAtelierChange(idx, partial)}
            onRemove={() => removeAtelierRow(idx)}
            groupes={muscleNames.length ? muscleNames : groupesMusculaires}
            ateliersEx={atelierNames.length ? atelierNames : exemplesAteliers}
            mode={settings.mode}
            mappings={mappings}
            color={a.color}
          />
        ))}

        <Box sx={{ mt: 1, mb: 2 }}>
          <Button variant="outlined" onClick={addAtelierRow}>Ajouter un atelier</Button>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Enregistrement...' : 'Enregistrer la séance'}
          </Button>

          {onOpenMonCarnet && (
            <Button
              variant="contained"
              startIcon={<MenuBookIcon />}
              onClick={onOpenMonCarnet}
              sx={{
                backgroundColor: '#E6D5FF',
                color: '#4B2166',
                fontWeight: 'bold',
                textTransform: 'none'
              }}
            >
              Voir mon carnet
            </Button>
          )}

          <Button
            variant="outlined"
            onClick={() => {
              setAteliers([{ groupe: '', atelierNom: '', series: '', repetitions: '', poids: '', ressenti: 1, color: pastelColors[0] }]);
              setMessage(null);
            }}
          >
            Réinitialiser
          </Button>
        </Box>

        {message && (
          <Box sx={{ mt: 2 }}>
            <Typography color={message.type === 'error' ? 'error' : 'green'}>{message.text}</Typography>
          </Box>
        )}
      </Box>

      <Dialog open={examplesOpen} onClose={() => setExamplesOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Exemples pour : {getSeanceTypeLabel(type)}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box
              component="img"
              src={exampleImageForType(type)}
              alt={`Exemples ${getSeanceTypeLabel(type)}`}
              sx={{ maxWidth: '100%', height: 'auto', borderRadius: 1 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExamplesOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}